import { FailureStreak, sleepWhile } from './failure-backoff.js';
import { consoleLogger } from '../ports/logger.js';
/** Short, stable code for a failed item; the message carries the detail. */
export function playbackErrorCode(error) {
    const code = error?.code;
    if (typeof code === 'number')
        return `MEDIA_ERR_${code}`;
    if (error instanceof Error && error.name && error.name !== 'Error')
        return error.name.slice(0, 64);
    return 'PLAYBACK_FAILED';
}
export function playbackErrorMessage(error) {
    return (error instanceof Error ? error.message : String(error)).slice(0, 500);
}
function yieldEventLoop() {
    return new Promise((resolve) => setTimeout(resolve, 0));
}
/**
 * Loops the active playlist's verified local items through a PlaybackEngine.
 *
 * Each `playPlaylist` call owns a run id so a later `stop()` / restart cannot
 * revive an older loop that was mid-await when `stopped` flipped back to false.
 */
export class PlaylistPlayer {
    engine;
    assets;
    resolveNetworkUrl;
    playbackEvents;
    timing;
    stopped = true;
    runId = 0;
    logger;
    constructor(engine, assets, logger, resolveNetworkUrl, playbackEvents, timing = {}) {
        this.engine = engine;
        this.assets = assets;
        this.resolveNetworkUrl = resolveNetworkUrl;
        this.playbackEvents = playbackEvents;
        this.timing = timing;
        this.logger = logger ?? consoleLogger;
    }
    async playPlaylist(playlist) {
        const runId = ++this.runId;
        const serverNow = this.timing.serverNow ?? (() => Date.now());
        const monotonicNow = this.timing.monotonicNow ?? (() => performance.now());
        this.stopped = false;
        const passFailures = new FailureStreak();
        const itemFailures = new Map();
        while (!this.stopped && this.runId === runId) {
            let playedThisPass = false;
            for (const [position, item] of playlist.manifest.items.entries()) {
                if (this.stopped || this.runId !== runId)
                    return;
                const stored = await this.assets.get(item.assetId);
                if (!stored) {
                    this.logger.warn('Missing cached asset — skipping', { assetId: item.assetId });
                    continue;
                }
                const resolver = this.resolveNetworkUrl;
                const playable = {
                    ...item,
                    localUri: stored.localUri,
                    ...(resolver && item.mimeType.startsWith('video/')
                        ? { resolveNetworkUrl: () => resolver(playlist.ref, item) }
                        : {}),
                };
                const base = {
                    mediaAssetId: item.assetId,
                    playlistId: playlist.ref.playlistId,
                    playlistVersion: playlist.ref.version,
                    itemPosition: position,
                };
                let streak = itemFailures.get(position);
                if (!streak) {
                    streak = new FailureStreak();
                    itemFailures.set(position, streak);
                }
                const startedAt = monotonicNow();
                const startPlayedAt = new Date(serverNow()).toISOString();
                // While this item keeps failing, its start is held back and only recorded if it plays.
                const startDeferred = streak.failing;
                if (!startDeferred) {
                    this.playbackEvents?.record({ ...base, eventType: 'play_start', playedAt: startPlayedAt });
                }
                try {
                    await this.engine.play(playable);
                    const endedAt = monotonicNow();
                    if (startDeferred) {
                        this.playbackEvents?.record({ ...base, eventType: 'play_start', playedAt: startPlayedAt });
                    }
                    streak.succeed();
                    playedThisPass = true;
                    this.playbackEvents?.record({
                        ...base,
                        // Stopped or replaced while playing: the item did not finish.
                        eventType: this.stopped || this.runId !== runId ? 'play_interrupted' : 'play_end',
                        playedAt: new Date(serverNow()).toISOString(),
                        durationMs: Math.max(0, Math.round(endedAt - startedAt)),
                    });
                }
                catch (error) {
                    // One play_failed per failure streak of this item, not one per attempt.
                    if (streak.fail()) {
                        this.logger.warn('Playback item failed', {
                            assetId: item.assetId,
                            error: error instanceof Error ? error.message : String(error),
                        });
                        this.playbackEvents?.record({
                            ...base,
                            eventType: 'play_failed',
                            playedAt: new Date(serverNow()).toISOString(),
                            errorCode: playbackErrorCode(error),
                            errorMessage: playbackErrorMessage(error),
                        });
                    }
                }
                if (this.stopped || this.runId !== runId)
                    return;
            }
            if (playedThisPass) {
                passFailures.succeed();
                // Yield so stop() and sync can run when play() resolves instantly (images / stubs).
                await yieldEventLoop();
            }
            else {
                // Nothing played this pass (every item failed or is missing): back off
                // (5 s doubling to 60 s) instead of spinning.
                passFailures.fail();
                await sleepWhile(passFailures.backoffMs(), () => !this.stopped && this.runId === runId, this.timing.sleep);
            }
        }
    }
    async stop() {
        this.stopped = true;
        this.runId += 1;
        await this.engine.stop();
    }
}
//# sourceMappingURL=playlist-player.js.map